import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';

import api from 'api';
import useTranslate from './useTranslate';
import { downloadBlob } from 'utils/file';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { setAlert } from 'redux/alert';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { ApplicationDocumentInterface } from 'types';

const useDocuments = () => {
    const { translate } = useTranslate();
    const dispatch = useDispatch<AppDispatch>();

    const { entity: activeApp } = useSelector((state: RootState) => state.application);

    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isFetchingDocumentPreviewUrl, setIsFetchingDocumentPreviewUrl] =
        useState<boolean>(false);
    const [isFetchingTemplatePreviewUrl, setIsFetchingTemplatePreviewUrl] =
        useState<boolean>(false);

    const displayName = (doc: ApplicationDocumentInterface, candidateName: string) => {
        if (doc) {
            if ('name' in doc) {
                return candidateName + ' - ' + doc.name;
            } else if ('document_name' in doc) {
                return candidateName + ' - ' + doc.document_name;
            }
        }
        return translate('noNameAvailable');
    };

    const handleDownloadDoc = async (appId: string, doc: ApplicationDocumentInterface) => {
        try {
            setIsDownloading(true);

            const docId = doc.id;

            const resp = await api.documentResource.getBlobDocument(appId, docId);
            const fileName =
                resp.headers['resource_name'] ||
                `${displayName(doc, activeApp?.candidate_name ?? '')}.pdf`;
            downloadBlob(resp.data, fileName);
        } catch (error) {
            dispatch(
                setAlert({ message: getErrorMessage(error, 'Failed to download'), type: 'error' })
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const handleUploadDoc = async (
        file: File,
        doc: ApplicationDocumentInterface,
        appId?: string
    ): Promise<boolean> => {
        try {
            setIsUploading(true);

            const formData: FormData = new FormData();
            formData.append('document', file);

            await api.documentResource.post(doc.id, formData);

            const applicationId = appId || activeApp?.id?.toString();
            if (applicationId) {
                dispatch(loadDocs(applicationId));
            }
            return true;
        } catch (error) {
            dispatch(
                setAlert({ message: getErrorMessage(error, 'Failed to upload'), type: 'error' })
            );
            return false;
        } finally {
            setIsUploading(false);
        }
    };

    const getDocumentPreviewUrl = async (documentId: string) => {
        try {
            setIsFetchingDocumentPreviewUrl(true);
            const response = await api.documentResource.get(documentId);
            return response.data.url;
        } catch (error) {
            dispatch(
                setAlert({ message: getErrorMessage(error, 'Failed to upload'), type: 'error' })
            );
        } finally {
            setIsFetchingDocumentPreviewUrl(false);
        }
    };

    const getDocumentTemplatePreviewUrl = async (applicationId: string, documentId: number) => {
        try {
            setIsFetchingTemplatePreviewUrl(true);
            const response = await api.application.getDocumentTemplateUrl(
                applicationId,
                documentId
            );
            return response.data.url;
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to load template preview'),
                    type: 'error',
                })
            );
        } finally {
            setIsFetchingTemplatePreviewUrl(false);
        }
    };

    return {
        isDownloading,
        isUploading,
        isFetchingDocumentPreviewUrl,
        isFetchingTemplatePreviewUrl,
        handleDownloadDoc,
        handleUploadDoc,
        getDocumentPreviewUrl,
        getDocumentTemplatePreviewUrl,
    };
};

export default useDocuments;
